import {
  AcademicBackground,
  ContractType,
  DniType,
  EmploymentSituationName,
  MaritalStatus,
} from '@contexts/Estate/EstateApplication/domain/models';
import { IdentificationDocumentCode } from '@contexts/Payments/Shared/domain/models/types/identification-document-types.model';

export type Option<T> = Readonly<{
  name: string;
  value: T;
}>;

export enum DocumentTypes {
  CC = 'CC',
  PA = 'PA',
  CE = 'CE',
  CPEP = 'CPEP',
  NIT = 'NIT',
}

export enum MaritalStatusTypes {
  CASADO = 'CASADO',
  SOLTERO = 'SOLTERO',
  UNION_LIBRE = 'UNION_LIBRE',
  VIUDO = 'VIUDO',
  DIVORCIADO = 'DIVORCIADO',
  PERSONA_JURIDICA = 'PERSONA_JURIDICA',
}

export const MaritalStatusOptions: Option<MaritalStatus>[] = [
  {
    name: 'Casado',
    value: MaritalStatus.MARRIED,
  },
  {
    name: 'Soltero/a',
    value: MaritalStatus.SINGLE,
  },
  {
    name: 'Unión libre',
    value: MaritalStatus.FREE_UNION,
  },
  {
    name: 'Viudo/a',
    value: MaritalStatus.WIDOWER,
  },
  {
    name: 'Divorciado/a',
    value: MaritalStatus.DIVORCED,
  },
];

export const DocumentTypesOptions: Option<DniType>[] = [
  {
    name: DocumentTypes.CC,
    value: DniType.CC,
  },
  {
    name: DocumentTypes.PA,
    value: DniType.PASSPORT,
  },
  {
    name: DocumentTypes.CE,
    value: DniType.CE,
  },
  {
    name: DocumentTypes.NIT,
    value: DniType.NIT,
  },
  {
    name: DocumentTypes.CPEP,
    value: DniType.CPEP,
  },
];

export enum UserTypes {
  landlord = 'LANDLORD',
  tenant = 'TENANT',
  broker = 'CAPTURING_AGENT',
}

export interface TableColumnDef {
  Header: string;
  accessor: string;
}

export enum HighestAcademicLevel {
  BACHILLER = 'Bachiller',
  TECNICO_TECNOLOGICO = 'Técnico/Tecnológico',
  PREGRADO = 'Pregrado',
  MAESTRIA = 'Maestría',
  DOCTORADO = 'Doctorado',
  NINGUNA_DE_LAS_ANTERIORES = 'Ninguna de las anteriores',
}

export const HighestAcademicLevelOptions: Option<AcademicBackground>[] = [
  {
    name: HighestAcademicLevel.BACHILLER,
    value: AcademicBackground.BACHELOR,
  },
  {
    name: HighestAcademicLevel.TECNICO_TECNOLOGICO,
    value: AcademicBackground.TECHNICAL,
  },
  {
    name: HighestAcademicLevel.PREGRADO,
    value: AcademicBackground.UNDERGRADUATE,
  },
  {
    name: HighestAcademicLevel.MAESTRIA,
    value: AcademicBackground.MASTER_DEGREE,
  },
  {
    name: HighestAcademicLevel.DOCTORADO,
    value: AcademicBackground.PHD,
  },
  {
    name: HighestAcademicLevel.NINGUNA_DE_LAS_ANTERIORES,
    value: AcademicBackground.ANY,
  },
];

export enum EmploymentSituation {
  EMPLEADO = 'Empleado',
  INDEPENDIENTE = 'Independiente',
  PENSIONADO = 'Pensionado',
  RENTISTA = 'Rentista',
  PERSONA_JURIDICA = 'Persona Jurídica',
  DESEMPLEADO = 'Desempleado',
}

export type TypeOfEmployee =
  | 'Employee'
  | 'Independent'
  | 'Pensioner'
  | 'Rentier'
  | 'Legal_Person'
  | 'Unemployed';

export const EmploymentSituationOptions: Option<EmploymentSituationName>[] = [
  {
    name: EmploymentSituation.EMPLEADO,
    value: EmploymentSituationName.EMPLOYED,
  },
  {
    name: EmploymentSituation.INDEPENDIENTE,
    value: EmploymentSituationName.INDEPENDENT,
  },
  {
    name: EmploymentSituation.PENSIONADO,
    value: EmploymentSituationName.PENSIONER,
  },
  {
    name: EmploymentSituation.RENTISTA,
    value: EmploymentSituationName.RENTIER,
  },
  {
    name: EmploymentSituation.PERSONA_JURIDICA,
    value: EmploymentSituationName.LEGAL_PERSON,
  },
  {
    name: EmploymentSituation.DESEMPLEADO,
    value: EmploymentSituationName.UNEMPLOYED,
  },
];

export const TypeOfContractOptions: Option<ContractType>[] = [
  {
    name: 'Por trabajo a término indefinido',
    value: ContractType.LONG_TERM,
  },
  {
    name: 'Por trabajo a término fijo',
    value: ContractType.FIXED_TERM,
  },
  {
    name: 'Por obra o labor',
    value: ContractType.BY_JOB,
  },
  {
    name: 'Contrato de Aprendizaje',
    value: ContractType.FOR_LEARNING,
  },
  {
    name: 'Contrato temporal, ocasional o accidental',
    value: ContractType.TEMPORARY,
  },
];

export const PaymentDocumentTypesOptions: Option<IdentificationDocumentCode>[] =
  [
    {
      name: 'Cédula de ciudadanía',
      value: IdentificationDocumentCode.CC,
    },
    {
      name: 'Cédula de extranjería',
      value: IdentificationDocumentCode.CE,
    },
    {
      name: 'Número de Identificación Tributaria',
      value: IdentificationDocumentCode.NIT,
    },
    {
      name: 'Pasaporte',
      value: IdentificationDocumentCode.PA,
    },
  ];

export enum CountryPhoneNumberCode {
  Colombia = '+57',
  Mexico = '+52',
  USA = '+1',
  Canada = '+1',
  España = '+34',
  Argentina = '+54',
  Chile = '+56',
  Panama = '+507',
  Peru = '+51',
  Ecuador = '+593',
  Brasil = '+55',
  Uruguay = '+598',
}

const countryFlagIconsExternalURL =
  'https://catamphetamine.gitlab.io/country-flag-icons/3x2';

export const CountryPhoneNumberCodeOptions: Option<CountryPhoneNumberCode>[] = [
  {
    name: `${countryFlagIconsExternalURL}/CO.svg`,
    value: CountryPhoneNumberCode.Colombia,
  },
  {
    name: `${countryFlagIconsExternalURL}/MX.svg`,
    value: CountryPhoneNumberCode.Mexico,
  },
  {
    name: `${countryFlagIconsExternalURL}/AR.svg`,
    value: CountryPhoneNumberCode.Argentina,
  },
  {
    name: `${countryFlagIconsExternalURL}/BR.svg`,
    value: CountryPhoneNumberCode.Brasil,
  },
  {
    name: `${countryFlagIconsExternalURL}/CA.svg`,
    value: CountryPhoneNumberCode.Canada,
  },
  {
    name: `${countryFlagIconsExternalURL}/CL.svg`,
    value: CountryPhoneNumberCode.Chile,
  },
  {
    name: `${countryFlagIconsExternalURL}/EC.svg`,
    value: CountryPhoneNumberCode.Ecuador,
  },
  {
    name: `${countryFlagIconsExternalURL}/ES.svg`,
    value: CountryPhoneNumberCode.España,
  },
  {
    name: `${countryFlagIconsExternalURL}/PA.svg`,
    value: CountryPhoneNumberCode.Panama,
  },
  {
    name: `${countryFlagIconsExternalURL}/PE.svg`,
    value: CountryPhoneNumberCode.Peru,
  },
  {
    name: `${countryFlagIconsExternalURL}/US.svg`,
    value: CountryPhoneNumberCode.USA,
  },
  {
    name: `${countryFlagIconsExternalURL}/UY.svg`,
    value: CountryPhoneNumberCode.Uruguay,
  },
];
